/**
 * A module's run: what a learner has finished, and where they are now.
 *
 * A **capsule** is a film and then its questions (see `#/lib/capsuleCover` and
 * `CapsuleThemePlayer`), and a **module** is six or more capsules one after
 * another. So playing a module is not six quizzes in a row and it is not six
 * films in a row: it is the same thing the feed plays, six times over, and the run
 * is the glue between them.
 *
 * ## The two rules this file exists for
 *
 *  - **A capsule opens on its film, and the questions come only when the film is
 *    over.** A capsule whose film was never uploaded — a room's own capsule, or one
 *    published before covers existed — opens on its questions instead, because
 *    waiting for a film that does not exist is a black screen with no way forward.
 *    That is the same rule the capsule player already follows, and it is why
 *    `moduleOpeningStage` asks a question rather than assuming.
 *  - **Finishing a capsule's questions leads into the *next* capsule's film** —
 *    never to a summary, and never through a screen that says the module is over
 *    when it is not. The summary belongs to the end of the playlist and to nowhere
 *    else, which is exactly the bug the first build had: it drew *Module finished*
 *    whenever the next capsule's record was still on its way, so a learner mid-run
 *    was told their course was done between every pair of capsules.
 *
 * ## Why the progress is kept on the device
 *
 * A run is long — six films and six sets of questions — and nobody does that in
 * one sitting. The plays themselves are posted per capsule (`createPlay`), so the
 * class's results live on the service; but the *run* — which capsules of this
 * playlist this learner has finished — has no endpoint yet, because
 * `app.bsky.feed.getCapsulePlays` does not exist. So the run is remembered where
 * the app remembers everything else it can only know locally (`#/state/persisted`,
 * the same store the classrooms use), keyed by module and learner, and it is what
 * makes *resume* possible today. When the plays endpoint lands, the same shape can
 * be derived from the class's own plays instead — the two answers being equal is a
 * property worth asserting then, not a guess to make now.
 *
 * Everything here is pure: the rules, the lookups and the merges. The storage and
 * the React binding are one layer up (`#/components/modules/useModuleRun`).
 */
import {z} from 'zod'

/** Which half of a capsule is on screen. */
export type ModuleRunStage = 'film' | 'quiz'

/** Where a run is: a capsule, and which half of it. */
export type ModuleRunStep = {
  /** Zero-based position in the playlist. */
  index: number
  stage: ModuleRunStage
}

/**
 * What a capsule opens on.
 *
 * `hasFilm` is asked of the capsule's own cover, not of the playlist: a capsule
 * without one opens on its questions rather than on nothing.
 */
export function moduleOpeningStage(hasFilm: boolean): ModuleRunStage {
  return hasFilm ? 'film' : 'quiz'
}

/**
 * The film is over: this capsule's questions.
 *
 * The index does not move. Reading a film is how you *reach* the questions, so a
 * step that skipped a capsule here would skip the work the film was introducing.
 */
export function moduleStepAfterFilm(index: number): ModuleRunStep {
  return {index: Math.max(0, Math.floor(index)), stage: 'quiz'}
}

/**
 * The questions are over: the next capsule's film, or the end of the run.
 *
 * The next capsule always opens on its *film*, not on its questions, whatever
 * that film turns out to be — the player asks `moduleOpeningStage` again when the
 * record arrives, and a capsule with no film corrects itself one frame later. The
 * alternative, deciding here from what the previous capsule looked like, is how a
 * playlist starts reading as a list of quizzes.
 *
 * Returns `null` for *the run is over*, which is the only thing that leads to the
 * summary.
 */
export function moduleStepAfterQuiz(
  index: number,
  count: number,
): ModuleRunStep | null {
  const next = Math.max(0, Math.floor(index)) + 1
  if (next >= count) return null
  return {index: next, stage: 'film'}
}

/** How much of a module is behind the learner. */
export type ModuleRunProgress = {
  /** Capsules of this playlist whose questions have been finished. */
  done: number
  total: number
  /** Every capsule is done. */
  finished: boolean
  /**
   * Where to continue: the first capsule not yet finished, or the first capsule
   * of a run that has been left incomplete at the end (which is how a learner
   * replays a module they have already finished).
   */
  resumeIndex: number
}

/**
 * The state of a run.
 *
 * `done` counts only the playlist's *own* capsules: a record of something that is
 * no longer in the module — a capsule the creator removed — must not make six of
 * six out of five, which is why the count is a filter over the playlist rather
 * than the length of what was stored.
 */
export function moduleRunProgress({
  items,
  done,
}: {
  items: readonly {uri: string}[]
  /** The capsule uris this learner has finished here. */
  done: readonly string[]
}): ModuleRunProgress {
  const finished = new Set(done)
  const played = items.filter(item => finished.has(item.uri)).length
  const total = items.length
  return {
    done: played,
    total,
    finished: total > 0 && played >= total,
    resumeIndex: moduleResumeIndex(items, done),
  }
}

/**
 * How far into a run the finish line starts to feel near, in capsules.
 *
 * The number is the *floor*: from here on — or from halfway, whichever comes
 * first — the card is allowed to shout. Below it a learner who has played one
 * capsule of seven is still being *encouraged*; above it they are being *raced*,
 * because a run that close to done is one sitting away from a rank (see
 * `moduleRunIsClose` and the hot button on the card).
 */
export const MODULE_RUN_CLOSE_DONE = 3

/**
 * Whether what is left of a run is small enough to shout about.
 *
 * Two ways to be close, and either one counts: **three capsules done**, so the
 * habit exists, or **half the run done**, so a short module is not made to feel
 * farther than it is. A finished run is never close — there is nothing left to
 * race for, and the card says so in words rather than in fire.
 */
export function moduleRunIsClose(progress: {
  done: number
  total: number
  finished: boolean
}): boolean {
  if (progress.finished || progress.total === 0) return false
  return (
    progress.done >= MODULE_RUN_CLOSE_DONE ||
    progress.done * 2 >= progress.total
  )
}

/**
 * Where a run continues from.
 *
 * The first capsule the learner has not finished — which is what makes *play some
 * of it now, the rest later* work without a menu and without a bookmark screen.
 * A playlist with nothing left in it starts again from the top, because pressing
 * *Play* on a finished module means playing it.
 */
export function moduleResumeIndex(
  items: readonly {uri: string}[],
  done: readonly string[],
): number {
  const finished = new Set(done)
  const index = items.findIndex(item => !finished.has(item.uri))
  return index === -1 ? 0 : index
}

/**
 * One learner's run of one module, as it is stored.
 *
 * `did` is `''` for a signed-out reader: the run is still worth keeping — the
 * plays behind it are the device's own experience of the module — and a signed-out
 * run must not be handed to the next account that signs in, which is what the key
 * is for.
 */
export const moduleRunSchema = z.object({
  moduleUri: z.string(),
  did: z.string(),
  /** The playlist's capsule uris whose questions have been finished, in order. */
  done: z.array(z.string()),
  updatedAt: z.string(),
})
export type ModuleRun = z.infer<typeof moduleRunSchema>

export const MODULE_RUNS_VERSION = 1

export const moduleRunsStateSchema = z.object({
  version: z.number(),
  items: z.array(moduleRunSchema),
})
export type ModuleRunsState = z.infer<typeof moduleRunsStateSchema>

export const MODULE_RUNS_DEFAULT: ModuleRunsState = {
  version: MODULE_RUNS_VERSION,
  items: [],
}

/** One run per module per learner, found by the pair. */
export function findModuleRun(
  state: ModuleRunsState | undefined,
  moduleUri: string,
  did: string,
): ModuleRun | undefined {
  return state?.items.find(
    run => run.moduleUri === moduleUri && run.did === did,
  )
}

/**
 * The stored runs with one written into them.
 *
 * Replaced rather than appended when the pair is already there, so a learner who
 * finishes a capsule twice — replaying a module, or on a second device — has one
 * row and not two. A `done` that has emptied is *removed*: a run with nothing in it
 * is not a state anybody means to be in, and keeping it would leave a stale row per
 * module a learner ever opened.
 */
export function upsertModuleRun(
  state: ModuleRunsState | undefined,
  run: ModuleRun,
): ModuleRunsState {
  const items = state?.items ?? []
  const rest = items.filter(
    other => !(other.moduleUri === run.moduleUri && other.did === run.did),
  )
  return {
    version: MODULE_RUNS_VERSION,
    items: run.done.length > 0 ? [...rest, run] : rest,
  }
}
